import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DownloadIcon from "@mui/icons-material/Download";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ShieldIcon from "@mui/icons-material/Shield";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import { fetchMySubscription, payMySubscription } from "../../../services/subscription";

const currencyFormat = (value?: number, currency = "USD") => {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
};

const dateFormat = (value?: string | Date) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return String(value);
  }
};

const statusColor: Record<string, "default" | "success" | "warning" | "error" | "primary"> = {
  active: "success",
  past_due: "warning",
  canceled: "default",
  trialing: "primary",
  suspended: "error",
  paused: "warning",
};

type Bill = {
  _id: string;
  invoiceNumber?: string;
  status?: string;
  periodStart?: string;
  periodEnd?: string;
  amount?: number;
  currency?: string;
  payments?: Array<{ amount?: number }>;
  createdAt: string;
};

export default function ManageSubscriptionPage() {
  useProtectedRoute();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const { token } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [payload, setPayload] = useState<any>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [periodUnit, setPeriodUnit] = useState<'month' | 'year' | 'day'>('month');
  const [periodCount, setPeriodCount] = useState<number>(1);
  const [compareOpen, setCompareOpen] = useState(false);
  const [autopay, setAutopay] = useState(false);
  const [defaultCurrency, setDefaultCurrency] = useState<'USD' | 'ZWL'>('USD');
  const [billingEmails, setBillingEmails] = useState('');
  const [billingWhatsApp, setBillingWhatsApp] = useState('');
  const [companyDetails, setCompanyDetails] = useState({ name: '', address: '', tax: '' });
  const [couponCode, setCouponCode] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState<'all' | 'paid' | 'overdue' | 'pending'>('all');

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchMySubscription(token);
        setPayload(data);
        setSelectedPlan(
          data?.subscription?.planCode ||
            data?.plan?.code ||
            data?.availablePlans?.[0]?.code ||
            null
        );
        setAutopay(Boolean(data?.subscription?.autopayEnabled));
        setDefaultCurrency(
          (data?.subscription?.currency || data?.plan?.currency || 'USD').toUpperCase() === 'ZWL'
            ? 'ZWL'
            : 'USD'
        );
        setBillingEmails(data?.billing?.emails?.join(', ') || data?.subscription?.billingEmail || '');
        setBillingWhatsApp(data?.billing?.whatsapp || '');
        setCompanyDetails({
          name: data?.billing?.companyName || '',
          address: data?.billing?.companyAddress || '',
          tax: data?.billing?.taxNumber || '',
        });
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load subscription');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const currentPlan = useMemo(() => {
    if (!payload) return null;
    if (payload.plan) return payload.plan;
    if (!payload.subscription?.planCode) return null;
    return payload.availablePlans?.find((p: any) => p.code === payload.subscription.planCode) || null;
  }, [payload]);

  const usageMetrics = useMemo(() => {
    const limits = payload?.usageLimits || currentPlan?.limits || {};
    const usage = payload?.usage || {};
    return [
      { key: 'invoices', label: 'Invoices / month', value: usage.invoices ?? 0, limit: limits.invoices ?? 200 },
      { key: 'storage', label: 'Storage (GB)', value: usage.storage ?? 6, limit: limits.storage ?? 50 },
      { key: 'users', label: 'Seats used', value: usage.users ?? payload?.subscription?.seats ?? 5, limit: limits.users ?? currentPlan?.maxUsers ?? 25 },
    ];
  }, [payload, currentPlan]);

  const bills: Bill[] = useMemo(() => payload?.bills || [], [payload]);

  const filteredBills = useMemo(() => {
    return bills.filter((bill) => {
      const term = invoiceFilter.trim().toLowerCase();
      const matchesTerm =
        !term ||
        bill.invoiceNumber?.toLowerCase().includes(term) ||
        dateFormat(bill.createdAt).toLowerCase().includes(term) ||
        (bill.status || '').toLowerCase().includes(term);
      const matchesStatus =
        methodFilter === 'all' ||
        (methodFilter === 'paid' && bill.status === 'paid') ||
        (methodFilter === 'overdue' && bill.status === 'overdue') ||
        (methodFilter === 'pending' && bill.status !== 'paid');
      return matchesTerm && matchesStatus;
    });
  }, [bills, invoiceFilter, methodFilter]);

  const planMatrix = useMemo(() => {
    const plans = payload?.availablePlans || [];
    const features = new Set<string>();
    plans.forEach((plan: any) => (plan.features || []).forEach((feature: string) => features.add(feature)));
    return { plans, features: Array.from(features) };
  }, [payload]);

  const handlePay = async () => {
    if (!token || !selectedPlan) return;
    try {
      setSaving(true);
      const data = await payMySubscription(token, {
        planCode: selectedPlan,
        periodUnit,
        periodCount,
        method: 'manual',
      });
      setPayload(data);
      setSnackbar({ open: true, message: 'Subscription updated successfully.', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.response?.data?.message || 'Payment failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleAutopayToggle = (value: boolean) => {
    setAutopay(value);
    setSnackbar({
      open: true,
      message: value
        ? 'Autopay enabled. We will charge your default method automatically.'
        : 'Autopay disabled. Pay invoices manually before due date.',
      severity: 'success',
    });
  };

  const handleRedeem = () => {
    if (!couponCode.trim()) {
      setSnackbar({ open: true, message: 'Enter a coupon first.', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: `Coupon "${couponCode}" submitted for validation.`, severity: 'success' });
    setCouponCode('');
  };

  const handleExportCsv = () => {
    setSnackbar({ open: true, message: 'CSV export queued. Check your email shortly.', severity: 'success' });
  };

  const openSupport = (channel: 'email' | 'whatsapp') => {
    if (channel === 'email') {
      window.location.href = 'mailto:support@squareedge.co.zw?subject=Subscription%20Help';
    } else {
      window.open('https://wa.me/263772690161?text=Need%20help%20with%20my%20RepairSync%20subscription', '_blank');
    }
  };

  if (!token) {
    return (
      <Box p={4}>
        <Alert severity="error">You must be signed in to manage your subscription.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate(-1)}>Go Back</Button>
      </Box>
    );
  }

  const subscription = payload?.subscription;
  const planPrice = currentPlan?.priceMonthly ?? 0;
  const billingCurrency = currentPlan?.currency || subscription?.currency || 'USD';
  const trialDaysLeft = subscription?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(subscription.trialEndsAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  const outstandingAmount = payload?.nextInvoice?.amountDue ?? planPrice * periodCount;

  const graceBanner = subscription?.status === 'past_due' ? (
    <Alert icon={<WarningAmberIcon fontSize="inherit" />} severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
      Payment overdue. Service remains active for {payload?.gracePeriodDays ?? 5} more days while we retry your payment method.
    </Alert>
  ) : null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto", display: 'grid', gap: 3 }}>
      {graceBanner}

      <Paper
        elevation={theme.palette.mode === 'dark' ? 0 : 2}
        sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}
      >
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} mb={3}>
          <Box>
            <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight={800} gutterBottom>
              Manage Subscription
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Keep your workspace running smoothly. Update plans, payment methods, and billing preferences in one place.
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button startIcon={<CompareArrowsIcon />} variant="outlined" onClick={() => setCompareOpen(true)}>
              Compare Plans
            </Button>
            <Button startIcon={<HelpOutlineIcon />} variant="outlined" color="secondary" onClick={() => openSupport('email')}>
              Need Help?
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary">
                  Current Plan
                </Typography>
                {subscription?.status && (
                  <Chip
                    size="small"
                    label={subscription.status.replace('_', ' ')}
                    color={statusColor[subscription.status] || 'default'}
                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                  />
                )}
              </Box>
              <Typography variant="h6" fontWeight={800} sx={{ mt: 1 }}>
                {currentPlan?.name || 'No plan selected'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {currentPlan ? `${currencyFormat(planPrice, billingCurrency)} per month` : 'Pick a plan to unlock premium features.'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Next invoice</Typography>
                  <Typography variant="body2" fontWeight={600}>{dateFormat(payload?.nextInvoice?.date || subscription?.currentPeriodEnd)}</Typography>
                  <Typography variant="caption" color="text.secondary">Amount: {currencyFormat(outstandingAmount, billingCurrency)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Seats / limits</Typography>
                  <Typography variant="body2" fontWeight={600}>{subscription?.seats ?? usageMetrics[2]?.value ?? 0} / {currentPlan?.maxUsers ?? usageMetrics[2]?.limit}</Typography>
                  <Typography variant="caption" color="text.secondary">Upgrade to add more users.</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Current period</Typography>
                  <Typography variant="body2" fontWeight={600}>{dateFormat(subscription?.currentPeriodStart)} - {dateFormat(subscription?.currentPeriodEnd)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" display="block">Trial status</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {trialDaysLeft !== null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} remaining` : 'Trial ended'}
                  </Typography>
                </Grid>
              </Grid>

              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                <Button variant="contained" onClick={handlePay} disabled={!selectedPlan || saving}>
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Pay & Renew'}
                </Button>
                <Button startIcon={<PauseCircleOutlineIcon />} color="warning" variant="outlined">
                  Pause Plan
                </Button>
                <Button startIcon={<CancelOutlinedIcon />} color="error" variant="outlined">
                  Cancel Plan
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Usage & Limits
              </Typography>
              <Box display="grid" gap={2}>
                {usageMetrics.map((metric) => {
                  const limit = metric.limit || 1;
                  const percent = Math.min(100, Math.round((metric.value / limit) * 100));
                  const nearLimit = percent >= 90;
                  return (
                    <Box key={metric.key}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" fontWeight={600}>{metric.label}</Typography>
                        <Typography variant="caption" color={nearLimit ? 'warning.main' : 'text.secondary'}>
                          {metric.value} / {limit}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          mt: 0.5,
                          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 4,
                            backgroundColor: nearLimit ? theme.palette.warning.main : theme.palette.primary.main,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Payment Methods
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Support for EcoCash, cards, and bank transfer. Set a default method for autopay, or upload proof for manual payments.
        </Typography>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>EcoCash</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  STK Push
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Enter the payer EcoCash number and we will send a request they can approve on their phone.
                </Typography>
                <TextField fullWidth label="EcoCash Number" placeholder="e.g. +26377xxxxxxx" sx={{ mb: 2 }} />
                <Button variant="contained">Send STK Push</Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Status: Pending approval. Last update 2s ago.
                </Typography>
                <FormControlLabel control={<Switch />} label="Save as default payment method" sx={{ mt: 1 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Manual EcoCash Payment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Send the exact amount to <strong>+263 772 772 218</strong>. Use reference <strong>{payload?.manualRefs?.ecocash || `${subscription?._id || 'TENANT'}-${new Date().getFullYear()}`}</strong>.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Amount due: {currencyFormat(outstandingAmount, defaultCurrency)} (RBZ mid-rate).
                </Typography>
                <Box component="form" sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
                  <TextField label="Transaction ID" fullWidth />
                  <TextField label="Payer EcoCash Number" fullWidth />
                  <TextField label="Amount Paid" type="number" fullWidth />
                  <TextField label="Paid At" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} />
                  <Button component="label" variant="outlined">
                    Upload Proof of Payment
                    <input hidden accept="image/*,.pdf" type="file" />
                  </Button>
                  <Button variant="contained" color="secondary">Submit for Review</Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Cards (Visa / Mastercard)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Secure card payments via PCI compliant provider. We support 3-D Secure for safer checkout.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Saved Cards</Typography>
                  {(payload?.savedCards || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No cards on file. Add a card to enable automatic billing.
                    </Typography>
                  ) : (
                    (payload?.savedCards || []).map((card: any) => (
                      <Box key={card.id} display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5 }}>
                        <Box>
                          <Typography fontWeight={600}>{card.brand?.toUpperCase()} **** {card.last4}</Typography>
                          <Typography variant="caption" color="text.secondary">Exp {card.expMonth}/{card.expYear}</Typography>
                        </Box>
                        <Box display="flex" gap={1}>
                          <Button size="small" variant={card.isDefault ? 'contained' : 'outlined'}>Set Default</Button>
                          <Button size="small" color="error">Remove</Button>
                        </Box>
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>Add New Card</Typography>
                  <Typography variant="caption" color="text.secondary">Powered by a tokenized PSP. We never store card numbers.</Typography>
                  <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
                    <Box sx={{ height: 52, border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
                      Card Element Placeholder
                    </Box>
                    <FormControlLabel control={<Switch />} label="Save for autopay" />
                    <Button variant="contained">Add Card</Button>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Bank Transfer (USD & ZWL)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700}>USD Account Details</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                  Account Name: SquareEdge Solutions
                  {"\n"}Bank: First Capital Bank
                  {"\n"}Account No: 113456789
                  {"\n"}Branch: Avondale
                  {"\n"}SWIFT: FCBLZWHX
                  {"\n"}Reference: {(payload?.manualRefs?.bank || `TEN-${subscription?._id || 'A1'}-${new Date().getMonth() + 1}`)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={700}>Upload Proof of Payment</Typography>
                <Box component="form" sx={{ mt: 1.5, display: 'grid', gap: 1.5 }}>
                  <TextField label="Amount Paid" type="number" fullWidth />
                  <TextField label="Paid At" type="datetime-local" InputLabelProps={{ shrink: true }} fullWidth />
                  <Button component="label" variant="outlined">
                    Upload POP (PDF/JPG/PNG)
                    <input hidden accept="image/*,.pdf" type="file" />
                  </Button>
                  <Button variant="contained" color="secondary">Submit POP</Button>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={700}>Local Rails (Future)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel control={<Switch />} label="ZIPIT" />
            <FormControlLabel control={<Switch />} label="Zimswitch Card" />
            <FormControlLabel control={<Switch />} label="OneMoney" />
            <FormControlLabel control={<Switch />} label="TeleCash" />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Enable these once your PSP integration is live. They will appear in the customer portal automatically.
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Paper>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Billing Preferences
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Default Currency"
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value as 'USD' | 'ZWL')}
              helperText="Exchange rates sourced from RBZ daily bulletin."
            >
              <MenuItem value="USD">USD (United States Dollar)</MenuItem>
              <MenuItem value="ZWL">ZWL (Zimbabwe Dollar)</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={<Switch checked={autopay} onChange={(e) => handleAutopayToggle(e.target.checked)} />}
              label="Enable autopay on due date"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Uses your default method (EcoCash STK push or saved card).
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Billing emails"
              value={billingEmails}
              onChange={(e) => setBillingEmails(e.target.value)}
              helperText="Separate multiple emails with commas"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="WhatsApp for receipts"
              value={billingWhatsApp}
              onChange={(e) => setBillingWhatsApp(e.target.value)}
              placeholder="+26377xxxxxxx"
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Company details for tax invoices</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Legal name" value={companyDetails.name} onChange={(e) => setCompanyDetails((prev) => ({ ...prev, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Business address" value={companyDetails.address} onChange={(e) => setCompanyDetails((prev) => ({ ...prev, address: e.target.value }))} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Tax / VAT number" value={companyDetails.tax} onChange={(e) => setCompanyDetails((prev) => ({ ...prev, tax: e.target.value }))} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="h6" fontWeight={700}>Invoices & Receipts</Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField size="small" placeholder="Search invoices" value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)} />
            <TextField
              select
              size="small"
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value as typeof methodFilter)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
            <Button startIcon={<DownloadIcon />} onClick={handleExportCsv}>Export CSV</Button>
          </Box>
        </Box>
        {filteredBills.length === 0 ? (
          <Alert severity="info">No billing history yet. Once you start paying, your invoices will appear here.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBills.map((bill) => {
                  const paidTotal = (bill.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
                  const outstanding = Number(bill.amount || 0) - paidTotal;
                  const status = bill.status || (outstanding > 0 ? 'overdue' : 'paid');
                  return (
                    <TableRow key={bill._id} hover selected={status === 'overdue'}>
                      <TableCell>{new Date(bill.createdAt).toLocaleDateString()} {isSmDown ? '' : new Date(bill.createdAt).toLocaleTimeString()}</TableCell>
                      <TableCell>{bill.invoiceNumber || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          color={status === 'paid' ? 'success' : status === 'overdue' ? 'error' : status === 'refunded' ? 'default' : 'warning'}
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>{currencyFormat(Number(bill.amount || 0), bill.currency || defaultCurrency)}</TableCell>
                      <TableCell>{currencyFormat(paidTotal, bill.currency || defaultCurrency)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download PDF">
                          <IconButton size="small"><DownloadIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Email receipt">
                          <IconButton size="small"><EmailIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Send via WhatsApp">
                          <IconButton size="small"><WhatsAppIcon fontSize="inherit" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Coupons, Credits & Referrals
            </Typography>
            <TextField
              label="Redeem coupon"
              fullWidth
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              helperText="Promo, referral, or bulk payment codes"
            />
            <Button sx={{ mt: 1.5 }} variant="contained" onClick={handleRedeem}>Apply Coupon</Button>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600}>Account credits</Typography>
            <Typography variant="body2" color="text.secondary">{currencyFormat(payload?.credits?.balance ?? 0, defaultCurrency)} available.</Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" fontWeight={600}>Referral program</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Share your referral link. Earn credits when teams sign up.
            </Typography>
            <TextField fullWidth disabled value={payload?.referral?.link || 'https://repair360.app/r/your-code'} />
            <Button sx={{ mt: 1.5 }} variant="outlined">Copy link</Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, height: '100%', display: 'grid', gap: 2 }}>
            <Typography variant="h6" fontWeight={700}>Dunning & Grace-Period Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              We automatically retry failed payments on day 3, 5, and 7. You can adjust messaging and autopause behaviour.
            </Typography>
            <TextField
              select
              label="Grace period (days)"
              fullWidth
              defaultValue={payload?.gracePeriodDays ?? 5}
            >
              <MenuItem value={3}>3 days</MenuItem>
              <MenuItem value={5}>5 days</MenuItem>
              <MenuItem value={7}>7 days</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch defaultChecked />} label="Show grace-period banner to admins" />
            <FormControlLabel control={<Switch />} label="Auto-downgrade to free plan when grace ends" />
            <Typography variant="caption" color="text.secondary">
              Customers receive reminder emails and WhatsApp notifications before any lockout occurs.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Security & Audit Trail
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sensitive actions require verification. Card data is vaulted by our PCI-DSS compliant provider.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <ShieldIcon color="primary" />
              <Box>
                <Typography fontWeight={600}>Two-factor approvals</Typography>
                <Typography variant="body2" color="text.secondary">We send a code when cancelling, refunding, or changing autopay.</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <ShieldIcon color="primary" />
              <Box>
                <Typography fontWeight={600}>Masked identifiers</Typography>
                <Typography variant="body2" color="text.secondary">EcoCash numbers and cards display as ****1234 to protect customer data.</Typography>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <ShieldIcon color="primary" />
              <Box>
                <Typography fontWeight={600}>Audit log</Typography>
                <Typography variant="body2" color="text.secondary">Track who changed plans, added cards, or applied coupons.</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Danger Zone
            </Typography>
            <Alert severity="warning" icon={<ShieldIcon fontSize="inherit" />} sx={{ mb: 2 }}>
              Cancelling stops future billing. Your workspace enters read-only mode after the current period ends.
            </Alert>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button variant="contained" color="warning">Pause billing (keeps data)</Button>
              <Button variant="outlined" color="error">Cancel subscription</Button>
              <Button variant="text" onClick={() => setSnackbar({ open: true, message: 'Downgrade requested. An agent will review shortly.', severity: 'success' })}>
                Request downgrade assistance
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, display: 'grid', gap: 1.5 }}>
            <Typography variant="h6" fontWeight={700}>Help & Support</Typography>
            <Typography variant="body2" color="text.secondary">
              Having trouble with EcoCash approvals or bank POPs? Check our quick tips or message the team directly.
            </Typography>
            <Button startIcon={<EmailIcon />} variant="outlined" onClick={() => openSupport('email')}>
              Email support
            </Button>
            <Button startIcon={<WhatsAppIcon />} variant="contained" color="success" onClick={() => openSupport('whatsapp')}>
              WhatsApp us
            </Button>
            <Typography variant="caption" color="text.secondary">Available Monday to Friday, 08:00 - 17:00 CAT.</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={compareOpen} onClose={() => setCompareOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Compare Plans</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                {planMatrix.plans.map((plan: any) => (
                  <TableCell key={plan.code}>{plan.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {planMatrix.features.map((_feature) => (
                <TableRow key={_feature}>
                  <TableCell>{_feature}</TableCell>
                  {planMatrix.plans.map((plan: any) => (
                    <TableCell key={plan.code}>
                      {(plan.features || []).includes(_feature) ? 'Yes' : 'No'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        ContentProps={{
          sx: {
            backgroundColor: snackbar.severity === 'success' ? theme.palette.success.main : theme.palette.error.main,
          },
        }}
      />
    </Box>
  );
}
